import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { CampaignAnalyticsService } from "@analytics/index";

import {
  applyNewTemplate,
  applySavedTemplate,
  applyStandardTemplate,
  CampaignAttachmentsType,
  cancelScheduledCampaign,
  createCampaign,
  deleteCampaign,
  generateEmails,
  getCampaign,
  getCampaigns,
  getPresignedUrls,
  handleAttachmentUpload,
  handleDeleteAttachment,
  handleUpdateEmail,
  removeEmails,
  saveEmails,
  sendCampaign,
  sendEmail,
  updateCampaign,
} from "@api/campaign.api";

import C from "@constants/constants";
import routes from "@constants/routes";

import { AttachmentType, CampaignType, EmailType } from "@interfaces/campaign.type";
import { WithRequiredPropertyType } from "@interfaces/typeHelpers";

import useCampaignEmailStore from "@store/campaignStore";
import { OrganizationStore } from "@store/organizationStore";
import { PopupActions } from "@store/popupStore";

interface ICreateCampaignInput {
  contactIds: string[];
  technologyId: string;
}

export const useCampaignsQuery = (technologyId?: string) => {
  const navigate = useNavigate();
  const organization = OrganizationStore.getState().organization;
  return useQuery({
    queryFn: async () => {
      if (!technologyId) {
        navigate(routes.PORTFOLIO);
        return;
      }
      const { body, status } = await getCampaigns(technologyId);
      if (status === 403) {
        // Conditionally navigate based on the existence of an organization
        if (organization) {
          navigate(`${routes.ORGANIZATION}/${organization.id}`); // Redirect to the organization page
        } else {
          navigate(`${routes.PORTFOLIO}`); // Redirect to the portfolio page
        }
        throw new Error("Redirecting due to 403 error");
      }

      return body;
    },
    queryKey: ["campaigns", technologyId],
    staleTime: C.STALE_TIME,
  });
};

interface IDeleteDraftCampaignInput {
  campaignId: string;
  technologyId: string;
}

export const useDeleteDraftCampaignQuery = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, IDeleteDraftCampaignInput, CampaignType[]>({
    mutationFn: async ({ campaignId }) => {
      await deleteCampaign(campaignId);
    },
    onError: (_, { technologyId }, campaigns) => {
      if (campaigns) {
        queryClient.setQueryData(["campaigns", technologyId], campaigns);
      }
      PopupActions.openNotification({
        content: "There was an issue deleting your Campaign. Please try again.",
        title: "Error",
      });
    },
    onMutate: ({ campaignId, technologyId }) => {
      const oldData = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);
      queryClient.setQueryData(["campaigns", technologyId], (oldData: CampaignType[] | undefined) =>
        oldData ? oldData.filter((campaign) => campaign.id !== campaignId) : oldData,
      );
      return oldData;
    },
    onSuccess: (_, { campaignId }) => {
      PopupActions.openAlert({
        duration: 4000,
        message: "Campaign Draft deleted.",
        severity: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });
    },
  });
};

export const useCampaignQuery = (campaignId?: string) => {
  const navigate = useNavigate();
  const organization = OrganizationStore.getState().organization;
  return useQuery({
    queryFn: async () => {
      if (!campaignId) {
        navigate(routes.PORTFOLIO);
        return;
      }
      const { body, status } = await getCampaign(campaignId);
      if (status === 403) {
        // Conditionally navigate based on the existence of an organization
        if (organization) {
          navigate(`${routes.ORGANIZATION}/${organization.id}`); // Redirect to the organization page
        } else {
          navigate(`${routes.PORTFOLIO}`); // Redirect to the portfolio page
        }
        throw new Error("Redirecting due to 403 error");
      }

      return body;
    },
    queryKey: ["campaigns", campaignId],
    staleTime: C.STALE_TIME,
  });
};

const useUpdateCampaignList = () => {
  const queryClient = useQueryClient();
  return (campaign: Partial<CampaignType>, technologyId?: string, create?: boolean) => {
    const campaignList = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);
    if (campaignList && create) {
      queryClient.setQueryData(["campaigns", technologyId], [...campaignList, campaign]);
    } else if (campaignList && technologyId) {
      queryClient.setQueryData(["campaigns", campaign.technologyId], (oldData: CampaignType[]) => {
        return oldData.map((oldCampaign) => {
          if (oldCampaign.id === campaign.id) {
            return campaign;
          }
          return oldCampaign;
        });
      });
    }
  };
};

export const useCreateCampaignQuery = () => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const updateCampaignList = useUpdateCampaignList();

  return useMutation<CampaignType, Error, ICreateCampaignInput, void>({
    mutationFn: async ({ contactIds, technologyId }) => {
      return await createCampaign(contactIds, technologyId);
    },
    onError: () => {
      PopupActions.openNotification({
        content: "There was an issue creating a new Campaign. Please try again.",
        title: "Error",
      });
    },
    onSuccess: (campaign, { contactIds, technologyId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });
      updateCampaignList(campaign, technologyId, true);
      CampaignAnalyticsService.createCampaign(technologyId, contactIds.length);
      queryClient.setQueryData(["campaigns", campaign.id], campaign);
      navigate(`${routes.CREATE_CAMPAIGN}/${campaign.id}/account`);
    },
  });
};

const getUpdateCampaignConfig = () => ({
  applyNewTemplate: {
    analyticsFunction: (campaign: WithRequiredPropertyType<CampaignType, "id">) =>
      CampaignAnalyticsService.applyNewTemplate(campaign.id),
    errorMessage: () =>
      PopupActions.openAlert({
        message: "There was an issue generating the email template. Please try again.",
        severity: "error",
      }),
    successMessage: () =>
      PopupActions.openAlert({ duration: 4000, message: "Email template generated.", severity: "success" }),
    updateFunction: (campaign: Partial<CampaignType>) => applyNewTemplate(campaign),
  },
  applySavedTemplate: {
    analyticsFunction: (campaign: WithRequiredPropertyType<CampaignType, "id">) =>
      CampaignAnalyticsService.applySavedTemplate(campaign.id),
    errorMessage: () =>
      PopupActions.openAlert({
        message: "There was an issue applying the email template. Please try again.",
        severity: "error",
      }),
    successMessage: () =>
      PopupActions.openAlert({ duration: 4000, message: "Email template applied.", severity: "success" }),
    updateFunction: (campaign: Partial<CampaignType>) => applySavedTemplate(campaign),
  },
  applyStandardTemplate: {
    analyticsFunction: (campaign: WithRequiredPropertyType<CampaignType, "id">) =>
      CampaignAnalyticsService.applyStandardTemplate(campaign.id),
    errorMessage: () =>
      PopupActions.openAlert({
        message: "There was an issue generating the email template. Please try again.",
        severity: "error",
      }),
    successMessage: () =>
      PopupActions.openAlert({ duration: 4000, message: "Email template generated.", severity: "success" }),
    updateFunction: (campaign: Partial<CampaignType>) => applyStandardTemplate(campaign),
  },
  generateEmails: {
    analyticsFunction: (campaign: WithRequiredPropertyType<CampaignType, "id">) =>
      CampaignAnalyticsService.generateEmails(campaign.id),
    errorMessage: () =>
      PopupActions.openNotification({
        content: "There was an issue generating your Campaign emails. Please try again.",
        title: "Error",
      }),
    successMessage: () =>
      PopupActions.openAlert({ duration: 4000, message: "Campaign emails generated.", severity: "success" }),
    updateFunction: async (campaign: Partial<CampaignType>) => {
      return await generateEmails(campaign);
    },
  },
  saveEmails: {
    analyticsFunction: () => void 0,
    errorMessage: () =>
      PopupActions.openNotification({
        content: "There was an issue saving your Campaign emails. Please try again.",
        title: "Error",
      }),
    successMessage: () =>
      PopupActions.openAlert({ duration: 4000, message: "Campaign emails saved.", severity: "success" }),
    updateFunction: async (campaign: Partial<CampaignType>) => {
      return await saveEmails(campaign);
    },
  },
  updateCampaign: {
    analyticsFunction: (campaign: WithRequiredPropertyType<CampaignType, "id">) => void 0,
    errorMessage: () =>
      PopupActions.openAlert({
        message: "There was an issue saving the Campaign. Please try again.",
        severity: "error",
      }),
    successMessage: () => PopupActions.openAlert({ duration: 4000, message: "Campaign saved.", severity: "success" }),
    updateFunction: (campaign: Partial<CampaignType>) => updateCampaign(campaign),
  },
});

interface IUpdateCampaignConfigType extends Partial<CampaignType> {
  method: keyof ReturnType<typeof getUpdateCampaignConfig>;
}

export const useUpdateCampaignQuery = () => {
  const queryClient = useQueryClient();

  const updateCampaignList = useUpdateCampaignList();
  const updateCampaignConfig = getUpdateCampaignConfig();

  return useMutation<WithRequiredPropertyType<CampaignType, "id">, Error, IUpdateCampaignConfigType, void>({
    mutationFn: async ({ method, ...campaign }) => {
      return (await updateCampaignConfig[method].updateFunction(campaign)) as WithRequiredPropertyType<
        CampaignType,
        "id"
      >;
    },
    onError: (_, { method }) => {
      updateCampaignConfig[method].errorMessage();
    },
    onSuccess: (campaign, { method }) => {
      updateCampaignList(campaign, campaign.technologyId);
      queryClient.setQueryData(["campaigns", campaign.id], campaign);
      updateCampaignConfig[method].successMessage();
      updateCampaignConfig[method].analyticsFunction(campaign);
    },
  });
};

interface IRemoveEmailInput {
  campaignId: string;
  emailId: number;
}

export const useRemoveEmailQuery = () => {
  const queryClient = useQueryClient();
  const updateCampaignList = useUpdateCampaignList();
  return useMutation<CampaignType, Error, IRemoveEmailInput, CampaignType>({
    mutationFn: async ({ campaignId, emailId }) => {
      return await removeEmails(campaignId, emailId);
    },
    onError: (_, __, campaign) => {
      if (campaign) {
        queryClient.setQueryData(["campaigns", campaign.id], campaign);
      }
      PopupActions.openNotification({
        content: "There was an issue removing this contact from your campaign. Please try again.",
        title: "Error",
      });
    },
    onMutate: ({ campaignId, emailId }) => {
      const previousCampaign = queryClient.getQueryData<CampaignType>(["campaigns", campaignId]);
      if (previousCampaign) {
        queryClient.setQueryData(["campaigns", campaignId], {
          ...previousCampaign,
          emails: previousCampaign.emails.filter((email: EmailType) => email.id !== emailId),
        });
      }
      return previousCampaign;
    },
    onSuccess: (campaign) => {
      updateCampaignList(campaign, campaign.technologyId);
      queryClient.setQueryData(["campaigns", campaign.id], campaign);
      PopupActions.openAlert({ duration: 4000, message: "Email removed from Campaign.", severity: "success" });
    },
  });
};

interface ISendCampaignInput {
  campaignId: string;
  technologyId: string;
}

export interface ISendCampaignResponse {
  emails: {
    campaignEmailId: number;
    sendAt?: number;
  }[];
  failedEmailCount: number;
  failedEmails: EmailType[];
  sentEmailsCount: number;
}

export const useSendCampaignQuery = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { scheduledDateTime } = useCampaignEmailStore();

  return useMutation<ISendCampaignResponse, Error, ISendCampaignInput, CampaignType[]>({
    mutationFn: async ({ campaignId }) => {
      return await sendCampaign(campaignId, scheduledDateTime);
    },
    onError: (_, { campaignId, technologyId }) => {
      const previousCampaigns = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);
      if (previousCampaigns) {
        queryClient.setQueryData(["campaigns", technologyId], (oldData: CampaignType[]) => {
          return oldData?.map((campaign) => {
            if (campaign.id === campaignId) {
              return {
                ...campaign,
                failedToSend: true,
              };
            }
            return campaign;
          });
        });
      }

      queryClient.setQueryData(["campaigns", campaignId], (oldData: CampaignType) => {
        return {
          ...oldData,
          failedToSend: true,
        };
      });

      navigate(`${routes.CREATE_CAMPAIGN}/${campaignId}/confirmation`);
    },
    onMutate: ({ campaignId, technologyId }: ISendCampaignInput) => {
      const previousCampaigns = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);
      if (previousCampaigns) {
        queryClient.setQueryData(["campaigns", technologyId], (oldData: CampaignType[]) => {
          return oldData?.map((campaign) => {
            if (campaign.id === campaignId) {
              if (scheduledDateTime) {
                return {
                  ...campaign,
                  scheduledAt: scheduledDateTime,
                };
              }
              return {
                ...campaign,
                sentAt: new Date().toISOString(),
              };
            }
            return campaign;
          });
        });
      }

      queryClient.setQueryData(["campaigns", campaignId], (oldData: CampaignType) => {
        return {
          ...oldData,
          ...(scheduledDateTime ? { scheduledAt: scheduledDateTime } : { sentAt: new Date().toISOString() }),
        };
      });
      return previousCampaigns;
    },
    onSuccess: (data, { campaignId, technologyId }) => {
      const previousCampaigns = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);

      if (previousCampaigns) {
        queryClient.setQueryData(["campaigns", technologyId], (oldData: CampaignType[]) => {
          return oldData?.map((campaign) => {
            if (campaign.id === campaignId) {
              if (scheduledDateTime) {
                return {
                  ...campaign,
                  failedToSend: false,
                  scheduledAt: scheduledDateTime,
                };
              }
              return {
                ...campaign,
                failedToSend: false,
                sentAt: new Date().toISOString(),
                sentCount: data.sentEmailsCount,
              };
            }
            return campaign;
          });
        });
      }

      queryClient.setQueryData(["campaigns", campaignId], (oldData: CampaignType) => {
        return {
          ...oldData,
          emails: oldData.emails.map((oldEmail) => {
            const updatedEmail = data.emails.find((sentEmail) => sentEmail.campaignEmailId === oldEmail.id);

            const isFailed = data.failedEmails.findIndex((failedEmail) => failedEmail.id === oldEmail.id);

            if (isFailed !== -1) {
              return {
                ...oldEmail,
                failedToSend: true,
              };
            }

            if (updatedEmail) {
              return {
                ...oldEmail,
                failedToSend: false,
                sendAt: (updatedEmail.sendAt || 0) * 1000,
                sent: !updatedEmail.sendAt,
                sentAt: updatedEmail.sendAt ? null : new Date().toISOString(),
              };
            }
            return oldEmail;
          }),
          failedToSend: false,
          sentAt: new Date().toISOString(),
          sentCount: oldData.emails.length - data.failedEmailCount,
        };
      });

      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });

      CampaignAnalyticsService.sendCampaign(campaignId, data.emails.length);
      navigate(`${routes.TECHNOLOGY}/${technologyId}/campaigns`);
    },
  });
};

interface ICampaignAttachmentInput {
  campaignId: string;
  fileObjects: CampaignAttachmentsType;
  files: File[];
}

export const useHandleCampaignAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation<CampaignType, Error, ICampaignAttachmentInput, CampaignType>({
    mutationFn: async ({ campaignId, fileObjects, files }) => {
      const data = await getPresignedUrls(campaignId, fileObjects);

      const isSuccess = await handleAttachmentUpload(data.urls, files);

      if (!isSuccess) {
        throw new Error("Attachment upload failed");
      }

      return data.campaign;
    },
    onError: (_, { campaignId }, campaign) => {
      if (campaign) {
        queryClient.setQueryData(["campaigns", campaignId], campaign);
      }
      // TODO: delete all campaign attachments
      PopupActions.openNotification({
        content: "There was an issue uploading attachments. Please try again.",
        title: "Error",
      });
    },
    onSuccess: (campaign, { campaignId }) => {
      if (campaign) {
        queryClient.setQueryData(["campaigns", campaignId], campaign);
        CampaignAnalyticsService.saveAttachment(campaignId);
      }
    },
  });
};

interface IRemoveAttachmentInput {
  attachmentId: number;
  attachmentName: string;
  campaignId: string;
}

export const useRemoveAttachmentQuery = () => {
  const queryClient = useQueryClient();
  const updateCampaignList = useUpdateCampaignList();
  return useMutation<CampaignType, Error, IRemoveAttachmentInput, CampaignType>({
    mutationFn: async ({ attachmentId, attachmentName }) => {
      return await handleDeleteAttachment(attachmentId, attachmentName);
    },
    onError: (_, __, campaign) => {
      if (campaign) {
        queryClient.setQueryData(["campaigns", campaign.id], campaign);
      }
      PopupActions.openNotification({
        content: "There was an issue deleting the attachment. Please try again.",
        title: "Error",
      });
    },
    onMutate: ({ attachmentId, campaignId }) => {
      const previousCampaign = queryClient.getQueryData<CampaignType>(["campaigns", attachmentId]);
      if (previousCampaign) {
        queryClient.setQueryData(["campaigns", campaignId], {
          ...previousCampaign,
          attachments: previousCampaign.attachments.filter(
            (attachment: AttachmentType) => attachment.id !== attachmentId,
          ),
        });
      }
      return previousCampaign;
    },
    onSuccess: (campaign) => {
      updateCampaignList(campaign, campaign.technologyId);
      queryClient.setQueryData(["campaigns", campaign.id], campaign);
    },
  });
};

export const useUpdateEmailQuery = () => {
  const queryClient = useQueryClient();
  const updateCampaignList = useUpdateCampaignList();

  return useMutation<
    EmailType,
    Error,
    { campaignId: string; email: Partial<EmailType>; emailId: number },
    CampaignType
  >({
    mutationFn: async ({ campaignId, email, emailId }) => {
      return await handleUpdateEmail(campaignId, emailId, email);
    },
    onError: (_, __, campaign) => {
      if (campaign) {
        queryClient.setQueryData(["campaigns", campaign.id], campaign);
      }
      PopupActions.openNotification({
        content: "There was an issue updating the email. Please try again.",
        title: "Error",
      });
    },

    onMutate: ({ campaignId, email, emailId }) => {
      const previousCampaign = queryClient.getQueryData<CampaignType>(["campaigns", campaignId]);

      if (previousCampaign) {
        queryClient.setQueryData(["campaigns", campaignId], {
          ...previousCampaign,
          emails: previousCampaign.emails.map((item) => {
            return item.id === emailId
              ? {
                  ...item,
                  ...email,
                }
              : item;
          }),
        });
      }
      return previousCampaign;
    },
    onSuccess: (email) => {
      const campaign = queryClient.getQueryData<CampaignType>(["campaigns", email.campaignId]);
      if (campaign) {
        const updatedCampaign = {
          ...campaign,
          emails: campaign.emails.map((item) => (item.id === email.id ? email : item)),
        };
        updateCampaignList(updatedCampaign, updatedCampaign.technologyId);
        queryClient.setQueryData(["campaigns", updatedCampaign.id], updatedCampaign);
      }
      PopupActions.openAlert({ duration: 4000, message: "Email updated.", severity: "success" });
    },
  });
};

interface ISendEmailInput {
  emailId: number;
  subject: string;
}

export const useSendEmailQuery = () => {
  const queryClient = useQueryClient();
  return useMutation<EmailType, Error, ISendEmailInput, void>({
    mutationFn: async ({ emailId }) => {
      return await sendEmail(emailId);
    },
    onError: (_, { subject }) => {
      PopupActions.openAlert({
        duration: 4000,
        message: `Failed to send email to ${subject}. Please try again or contact support.`,
        severity: "error",
      });
    },
    onSuccess: (email) => {
      const campaign = queryClient.getQueryData<CampaignType>(["campaigns", email.campaignId]);
      if (campaign) {
        const updatedCampaign = {
          ...campaign,
          emails: campaign.emails.map((item) =>
            item.id === email.id
              ? {
                  ...item,
                  failedToSend: false,
                  sent: true,
                }
              : item,
          ),
        };

        queryClient.setQueryData(["campaigns", updatedCampaign.id], updatedCampaign);
      }

      PopupActions.openAlert({ duration: 4000, message: `Email sent to ${email.email}`, severity: "success" });
    },
  });
};

interface ICancelScheduledEmailInput {
  campaignId: string;
  technologyId: string;
}

export const useCancelScheduledEmailQuery = () => {
  const queryClient = useQueryClient();
  return useMutation<CampaignType, Error, ICancelScheduledEmailInput, CampaignType>({
    mutationFn: async ({ campaignId }) => {
      return await cancelScheduledCampaign(campaignId);
    },
    onError: (_, { campaignId, technologyId }, context) => {
      const previousCampaigns = queryClient.getQueryData<CampaignType[]>(["campaigns", technologyId]);
      if (previousCampaigns && context) {
        queryClient.setQueryData(["campaigns", technologyId], (oldData: CampaignType[]) => {
          return oldData?.map((campaign) => {
            if (campaign.id === campaignId) {
              return {
                ...campaign,
                scheduledAt: context.scheduledAt,
              };
            }
            return campaign;
          });
        });
      }
      queryClient.setQueryData(["campaigns", campaignId], (oldData: CampaignType) => {
        return {
          ...oldData,
          failedToSend: true,
        };
      });
      PopupActions.openAlert({
        duration: 4000,
        message: "Failed to cancel scheduled email. Please try again or contact support.",
        severity: "error",
      });
    },
    //* could be useful at some point
    // onMutate: ({ campaignId, technologyId }) => {
    //   const previousCampaigns = queryClient.getQueryData<ICampaign[]>(["campaigns", technologyId]);
    //   const previousCampaign = queryClient.getQueryData<ICampaign>(["campaigns", campaignId]);
    //   if (previousCampaigns) {
    //     queryClient.setQueryData(["campaigns", technologyId], (oldData: ICampaign[]) => {
    //       return oldData?.map((campaign) => {
    //         if (campaign.id === campaignId) {
    //           return {
    //             ...campaign,
    //             sendAt: null,
    //           };
    //         }
    //         return campaign;
    //       });
    //     });
    //   }
    //   queryClient.setQueryData(["campaigns", campaignId], (oldData: ICampaign) => {
    //     return {
    //       ...oldData,
    //       sendAt: null,
    //     };
    //   });
    //   return previousCampaign;
    // },
    onSuccess: (data) => {
      localStorage.setItem(`campaign-${data.id}-lastStep`, "personalize");
      queryClient.setQueryData(["campaigns", data.id], data);
      const previousCampaigns = queryClient.getQueryData<CampaignType[]>(["campaigns", data.technologyId]);
      if (previousCampaigns) {
        queryClient.setQueryData(
          ["campaigns", data.technologyId],
          previousCampaigns.map((campaign) => {
            if (campaign.id === data.id) {
              return data;
            }
            return campaign;
          }),
        );
      }
      PopupActions.openAlert({
        duration: 4000,
        message: "Your campaign send has been cancelled. You can now edit your campaign to re-schedule.",
        severity: "success",
      });
    },
  });
};
